import { useState } from "react";
import { Phone, Mail, MessageCircle, MapPin, Send, Building2, Hash, Globe, Cog } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { SITE, IMAGES } from "@/config/site";
import Section, { RevealItem } from "@/components/Section";
import FloatingObjects from "@/components/FloatingObjects";
import PageHead from "@/components/PageHead";

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [status, setStatus] = useState("");

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const buildMessage = () => {
    const lines = [
      `New enquiry — ${SITE.brand.en}`,
      `Name: ${form.name}`,
      form.company ? `Company: ${form.company}` : "",
      form.email ? `Email: ${form.email}` : "",
      form.phone ? `Phone: ${form.phone}` : "",
      form.service ? `Service: ${form.service}` : "",
      "",
      `Message:`,
      form.message,
    ].filter(Boolean);
    return lines.join("\n");
  };

  const submitWhatsApp = (e) => {
    e.preventDefault();
    if (!form.name || !form.message) {
      setStatus(lang === "ar" ? "يرجى تعبئة الاسم والرسالة." : "Please fill in name and message.");
      return;
    }
    const url = `https://wa.me/${SITE.contact.whatsapp}?text=${encodeURIComponent(buildMessage())}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setStatus(t.contact.form.success);
  };

  const submitEmail = (e) => {
    e.preventDefault();
    if (!form.name || !form.message) {
      setStatus(lang === "ar" ? "يرجى تعبئة الاسم والرسالة." : "Please fill in name and message.");
      return;
    }
    const subject = `New enquiry from ${form.name}${form.company ? " · " + form.company : ""}`;
    const body = buildMessage();
    const mailto = `mailto:${SITE.contact.primaryEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  };

  const inputClass =
    "w-full bg-navy-900/70 border border-white/10 focus:border-gold-500/60 focus:outline-none text-white placeholder:text-white/35 px-4 py-3 text-sm transition-colors";

  return (
    <>
      <PageHead customTitle={`${t.contact.title} · ${SITE.brand.en}`} customDesc={t.contact.subtitle} />

      <section className="relative pt-40 pb-12 overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0 -z-10">
          <img src={IMAGES.contact} alt="" className="w-full h-full object-cover opacity-25" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-900 via-navy-900/85 to-navy-900" />
          <FloatingObjects density="low" />
          <div className="absolute -bottom-32 -left-32 opacity-10">
            <Cog className="w-[460px] h-[460px] text-gold-500 animate-gear-slow" strokeWidth={0.4} />
          </div>
        </div>
        <div className="container-pad">
          <div className="max-w-3xl">
            <div className="text-overline mb-4">{t.contact.overline}</div>
            <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05]">
              {t.contact.title}
            </h1>
            <p className="mt-5 text-white/70 text-base md:text-lg max-w-2xl leading-relaxed">
              {t.contact.subtitle}
            </p>
            <p className="mt-3 text-metallic font-heading text-lg md:text-xl">{SITE.brand.ar}</p>
          </div>
        </div>
      </section>

      {/* Quick action cards */}
      <Section testId="contact-actions" className="!py-12">
        <div className="container-pad grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
          <RevealItem>
            <a
              href={`https://wa.me/${SITE.contact.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
              className="metallic-border-top group block p-6 glass hover:border-gold-500/40 transition-all"
            >
              <MessageCircle className="w-6 h-6 text-gold-500 group-hover:scale-110 transition-transform" />
              <div className="mt-4 text-overline">{t.contact.whatsapp}</div>
              <div dir="ltr" className="font-heading font-bold text-white text-lg mt-1">{SITE.contact.phones[0]}</div>
            </a>
          </RevealItem>
          <RevealItem>
            <a
              href={`tel:${SITE.contact.phones[0].replace(/\s/g, "")}`}
              data-testid="contact-call-btn"
              className="metallic-border-top group block p-6 glass hover:border-gold-500/40 transition-all"
            >
              <Phone className="w-6 h-6 text-gold-500 group-hover:scale-110 transition-transform" />
              <div className="mt-4 text-overline">{t.contact.callNow}</div>
              <div dir="ltr" className="font-heading font-bold text-white text-lg mt-1">{SITE.contact.phones[1]}</div>
            </a>
          </RevealItem>
          <RevealItem>
            <a
              href={`mailto:${SITE.contact.primaryEmail}`}
              data-testid="contact-email-btn"
              className="metallic-border-top group block p-6 glass hover:border-gold-500/40 transition-all"
            >
              <Mail className="w-6 h-6 text-gold-500 group-hover:scale-110 transition-transform" />
              <div className="mt-4 text-overline">{t.contact.emailUs}</div>
              <div className="font-heading font-bold text-white text-lg mt-1 break-all">{SITE.contact.primaryEmail}</div>
            </a>
          </RevealItem>
        </div>
      </Section>

      {/* Form + info grid */}
      <Section testId="contact-grid" className="!pt-4">
        <div className="container-pad grid lg:grid-cols-12 gap-8">
          <RevealItem className="lg:col-span-7">
            <form
              onSubmit={submitWhatsApp}
              data-testid="contact-form"
              className="glass-strong p-6 md:p-8 space-y-5"
            >
              <div className="text-overline">{lang === "ar" ? "نموذج التواصل" : "Enquiry Form"}</div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs text-white/60 mb-1.5 block">{t.contact.form.name} *</span>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={onChange("name")}
                    data-testid="contact-input-name"
                    className={inputClass}
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-white/60 mb-1.5 block">{t.contact.form.company}</span>
                  <input
                    type="text"
                    value={form.company}
                    onChange={onChange("company")}
                    data-testid="contact-input-company"
                    className={inputClass}
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-white/60 mb-1.5 block">{t.contact.form.email}</span>
                  <input
                    type="email"
                    value={form.email}
                    onChange={onChange("email")}
                    data-testid="contact-input-email"
                    className={inputClass}
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-white/60 mb-1.5 block">{t.contact.form.phone}</span>
                  <input
                    type="tel"
                    dir="ltr"
                    value={form.phone}
                    onChange={onChange("phone")}
                    data-testid="contact-input-phone"
                    className={inputClass}
                  />
                </label>
              </div>

              <label className="block">
                <span className="text-xs text-white/60 mb-1.5 block">{t.contact.form.service}</span>
                <select
                  value={form.service}
                  onChange={onChange("service")}
                  data-testid="contact-input-service"
                  className={inputClass}
                >
                  <option value="">{t.contact.form.servicePlaceholder}</option>
                  {t.contact.servicesList.map((s) => (
                    <option key={s} value={s} className="bg-navy-800">
                      {s}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-xs text-white/60 mb-1.5 block">{t.contact.form.message} *</span>
                <textarea
                  rows={5}
                  required
                  value={form.message}
                  onChange={onChange("message")}
                  data-testid="contact-input-message"
                  className={`${inputClass} resize-none`}
                />
              </label>

              <div className="flex flex-wrap gap-3 pt-1">
                <button
                  type="submit"
                  data-testid="contact-submit-whatsapp"
                  className="btn-primary"
                >
                  <Send className="w-4 h-4 rtl-flip" />
                  {t.contact.form.submit}
                </button>
                <button
                  type="button"
                  onClick={submitEmail}
                  data-testid="contact-submit-email"
                  className="btn-secondary"
                >
                  <Mail className="w-4 h-4" />
                  {t.contact.form.submitEmail}
                </button>
              </div>

              {status && (
                <div
                  data-testid="contact-form-status"
                  className="text-sm text-gold-500 border-t border-white/10 pt-4"
                >
                  {status}
                </div>
              )}

              {/*
                === BACKEND HOOK ===
                To connect this form to a PHP/MySQL backend on Hostinger later,
                replace the body of submitWhatsApp() with a fetch() POST to your
                /contact.php endpoint. Example:

                const res = await fetch('/contact.php', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(form),
                });
                if (res.ok) setStatus('Message sent!'); else setStatus('Error sending message.');
              */}
            </form>
          </RevealItem>

          <RevealItem className="lg:col-span-5 space-y-4">
            <div className="glass-strong p-6">
              <div className="text-overline mb-3">{lang === "ar" ? "معلومات الشركة" : "Company Info"}</div>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <Building2 className="w-4 h-4 text-gold-500 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-white/55 text-[11px] uppercase tracking-wider">{t.contact.info.company}</div>
                    <div className="text-white">{SITE.brand.en}</div>
                    <div className="text-metallic">{SITE.brand.ar}</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Hash className="w-4 h-4 text-gold-500 mt-0.5 shrink-0" />
                  <div className="font-mono text-xs">
                    <span className="text-white/55">{t.contact.info.cr}: </span>
                    <span className="text-white">{SITE.contact.cr}</span>
                    <span className="text-white/30 mx-2">|</span>
                    <span className="text-white/55">{t.contact.info.vat}: </span>
                    <span className="text-white">{SITE.contact.vat}</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-gold-500 mt-0.5 shrink-0" />
                  <div className="flex flex-col gap-1">
                    {SITE.contact.phones.map((p) => (
                      <a
                        key={p}
                        dir="ltr"
                        href={`tel:${p.replace(/\s/g, "")}`}
                        className="text-white/85 hover:text-gold-500"
                      >
                        {p}
                      </a>
                    ))}
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-gold-500 mt-0.5 shrink-0" />
                  <div className="flex flex-col gap-1">
                    {SITE.contact.emails.map((e) => (
                      <a key={e} href={`mailto:${e}`} className="text-white/85 hover:text-gold-500 break-all">
                        {e}
                      </a>
                    ))}
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-gold-500 mt-0.5 shrink-0" />
                  <div className="text-white/85">
                    {lang === "ar" ? SITE.contact.address.ar : SITE.contact.address.en}
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Globe className="w-4 h-4 text-gold-500 mt-0.5 shrink-0" />
                  <div className="font-mono text-xs text-white/85">{SITE.contact.website}</div>
                </li>
              </ul>
            </div>

            <div className="overflow-hidden border border-white/10">
              <iframe
                title="Elite Al Hijaz location"
                src={SITE.contact.mapsEmbed}
                className="w-full h-72 grayscale-[30%] contrast-110"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                data-testid="contact-map-iframe"
              />
            </div>
          </RevealItem>
        </div>
      </Section>
    </>
  );
}
